import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import axios from "axios";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Card, Button, Typography, Message, Popconfirm } from "@arco-design/web-react";
import { IconQuestionCircle } from "@arco-design/web-react/icon";

import { endpoints } from "../api/endpoints";
import { UserAvatar } from "./UserAvatar/UserAvatar";
import { routes } from "../routes/routes";
import { Meeting } from "../models/Meeting";
import { ProfilePreviewState } from "../models/Person";
import { RootState } from "../store/store";
import { formatMeetingDisplayTime } from "../utils/meetingDateTime";
import { getButtonColor, getButtonTextColor } from "../utils/cssVariables";

import "./MeetingCard.css";

type Props = {
  meeting: Meeting,
  onCancelMeeting: () => void;
}

export const MeetingCard = ({meeting, onCancelMeeting}: Props) => {
  const { t } = useTranslation();
  const user = useSelector((state: RootState) => state.user.user);

  const {mentor_user_id, mentee_user_id, mentee_name, mentor_name, id, display_time, start_time, mentee_avatar_url, mentor_avatar_url, meeting_url} = meeting;

  const otherPersonId: number = mentor_user_id === user?.id ? mentee_user_id || 0 : mentor_user_id || 0;
  const otherPersonName: string = mentor_user_id === user?.id ? mentee_name : mentor_name;
  const otherPersonAvatar: string = mentor_user_id === user?.id ? mentee_avatar_url : mentor_avatar_url;

  const [isOtherPersonMentor, setIsOtherPersonMentor] = useState<boolean | undefined>();
  const [otherPersonFirstName, setOtherPersonFirstName] = useState<string | undefined>();
  const [otherPersonLastName, setOtherPersonLastName] = useState<string | undefined>();

  const redirectRoute = isOtherPersonMentor ? routes.MENTOR : routes.MENTEE;

  const otherPersonNameParts = otherPersonName?.trim().split(/\s+/).filter(Boolean) ?? [];
  const profilePreview: ProfilePreviewState = {
    avatar_url: otherPersonAvatar,
    name: otherPersonFirstName ?? otherPersonNameParts[0],
    last_name: otherPersonLastName ?? (otherPersonNameParts.slice(1).join(" ") || undefined),
  };

  useEffect(() => {
    axios
      .get(`${endpoints.getPublicProfile}${otherPersonId}`)
      .then((response) => {
        setIsOtherPersonMentor(response.data.isMentor);
        if (response.data.name) {
          setOtherPersonFirstName(response.data.name);
        }
        if (response.data.last_name) {
          setOtherPersonLastName(response.data.last_name);
        }
      })
      .catch((error) => {
        console.error("Error fetching profile data:", error);
      });
  }, [otherPersonId]);

  return (
    <Card
      className="card-with-icon-hover upcoming-meeting-card"
      cover={
        <div className="upcoming-meeting-card__avatar-wrap">
          <UserAvatar
            avatarUrl={otherPersonAvatar}
            firstName={otherPersonFirstName ?? otherPersonNameParts[0]}
            lastName={otherPersonLastName ?? (otherPersonNameParts.slice(1).join(" ") || undefined)}
            size={160}
            shape="circle"
            alt="avatar"
          />
        </div>
      }
    >
      <div className="upcoming-meeting-card__title">
        <Typography.Title heading={4} className="upcoming-meeting-card__title-text">
          {t('home_page.meeting_with', {otherPersonName})}
        </Typography.Title>
      </div>
      <div className="upcoming-meeting-card__datetime">
        <Typography.Paragraph className="upcoming-meeting-card__datetime-text">
          {formatMeetingDisplayTime(start_time, display_time)}
        </Typography.Paragraph>
      </div>
      <div className="upcoming-meeting-card__join">
        {meeting_url ? (
          <Typography.Paragraph className="upcoming-meeting-card__join-text">
            <a href={meeting_url} target="_blank" rel="noopener noreferrer">
              {t('home_page.join_meeting')}
            </a>
          </Typography.Paragraph>
        ) : null}
      </div>
      <div className="upcoming-meeting-card__actions">
        <Popconfirm
          title={t('home_page.cancel_meeting_confirmation_message')}
          icon={<IconQuestionCircle/>}
          onOk={() => {
            axios
              .post(`${endpoints.cancelMeeting}`, {
                meeting_id: id,
              })
              .then((response) => {
                onCancelMeeting();
                Message.success({ content: t('notification_messages.meeting_canceled_success'), closable: true, duration: 3000});
              })
              .catch((error) => {
                console.error(error);
                Message.error(t('notification_messages.meeting_canceled_error'));
              });
          }}
          okText={t('common.confirm_cancel_button')}
          cancelText={t('common.decline_cancel_button')}
          okButtonProps={{
            size: "large",
            style: {
              color: getButtonTextColor(),
              backgroundColor: getButtonColor(),
            },
          }}
          cancelButtonProps={{
            size: "large",
            style: {
              color: getButtonTextColor(),
              backgroundColor: getButtonColor(),
            },
          }}
        >
          <Button
            size={"large"}
            shape={"round"}
          >
            {t('common.cancel_button')}
          </Button>
        </Popconfirm>
        <NavLink
          to={`${redirectRoute.replace(":id", otherPersonId.toString())}`}
          state={{ profilePreview }}
        >
          <Button
            style={{
              color: getButtonTextColor(),
              backgroundColor: getButtonColor(),
            }}
            size={"large"}
            shape={"round"}
          >
            {t('common.view_mentor_profile_button')}
          </Button>
        </NavLink>
      </div>
    </Card>
  );
};
